import type { ClosedPeriod, Season, Seasonality } from '@xingroute/schema'
import { useLocale } from '../locale/LocaleContext.js'
import { Button } from './ui/Button.js'
import { Input } from './ui/Field.js'
import { Badge } from './ui/Misc.js'

export const SEASONS: Season[] = ['spring', 'summer', 'autumn', 'winter']

const SEASON_LABELS: Record<Season, string> = {
  spring: '春',
  summer: '夏',
  autumn: '秋',
  winter: '冬',
}

const SEASON_LABELS_EN: Record<Season, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
}

const EMPTY_SEASONALITY: Seasonality = { best_seasons: [], closed_periods: [] }

export function seasonLabel(season: Season): string {
  return SEASON_LABELS[season]
}

export function seasonDisplayLabel(season: Season, locale: string): string {
  return locale.startsWith('zh') ? SEASON_LABELS[season] : SEASON_LABELS_EN[season]
}

export function seasonForMonth(month: number): Season {
  if (month >= 3 && month <= 5) return 'spring'
  if (month >= 6 && month <= 8) return 'summer'
  if (month >= 9 && month <= 11) return 'autumn'
  return 'winter'
}

export function activeClosedPeriod(
  seasonality: Seasonality | undefined,
  date = new Date(),
): ClosedPeriod | null {
  return seasonality?.closed_periods.find((period) => isDateInClosedPeriod(period, date)) ?? null
}

export function isSeasonalitySuitableForDate(
  seasonality: Seasonality | undefined,
  date = new Date(),
): boolean {
  if (activeClosedPeriod(seasonality, date)) return false
  const bestSeasons = seasonality?.best_seasons ?? []
  if (bestSeasons.length === 0) return true
  return bestSeasons.includes(seasonForMonth(date.getMonth() + 1))
}

export function formatSeasonalityForPreview(seasonality: Seasonality | undefined): string {
  if (!seasonality) return ''
  const best = seasonality.best_seasons.map(seasonLabel).join(' · ') || '全年'
  const closed = seasonality.closed_periods
    .map((period) =>
      [period.from, period.to, period.reason].filter((part) => part && part.length > 0).join(' · '),
    )
    .join('\n')
  return [`适合季节: ${best}`, closed ? `关闭期:\n${closed}` : '关闭期: 无'].join('\n')
}

export function RouteSeasonalityBlock({
  seasonality,
  date = new Date(),
  className = '',
}: {
  seasonality: Seasonality | undefined
  date?: Date
  className?: string
}) {
  const bestSeasons = seasonality?.best_seasons ?? []
  const closed = activeClosedPeriod(seasonality, date)
  if (bestSeasons.length === 0 && !closed) return null

  return (
    <div className={`space-y-2 ${className}`.trim()} data-testid="route-seasonality-block">
      {closed && (
        <div
          className="flex items-start gap-2 rounded-[var(--xr-radius-md)] border border-[var(--xr-danger)]/30 bg-[var(--xr-danger-soft)] px-3 py-2 text-xs font-medium text-[var(--xr-danger)]"
          role="status"
          data-testid="route-seasonality-closed-banner"
        >
          <span
            aria-hidden="true"
            className="mt-0.5 inline-block h-0 w-0 flex-none"
            style={{
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderBottom: '9px solid var(--xr-danger)',
            }}
          />
          <span>该线路本月份限期关闭{closed.reason ? ` · ${closed.reason}` : ''}</span>
        </div>
      )}
      {bestSeasons.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {bestSeasons.map((season) => (
            <Badge key={season} tone="brand">
              {seasonLabel(season)}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

export function RouteSeasonalityEditor({
  value,
  onChange,
}: {
  value: Seasonality | undefined
  onChange: (next: Seasonality) => void
}) {
  const { t } = useLocale()
  const seasonality = value ?? EMPTY_SEASONALITY

  function setBestSeason(season: Season, enabled: boolean) {
    const selected = new Set(seasonality.best_seasons)
    if (enabled) selected.add(season)
    else selected.delete(season)
    onChange({
      ...seasonality,
      best_seasons: SEASONS.filter((candidate) => selected.has(candidate)),
    })
  }

  function setClosedPeriod(index: number, patch: Partial<ClosedPeriod>) {
    onChange({
      ...seasonality,
      closed_periods: seasonality.closed_periods.map((period, i) =>
        i === index ? { ...period, ...patch } : period,
      ),
    })
  }

  function addClosedPeriod() {
    onChange({
      ...seasonality,
      closed_periods: [...seasonality.closed_periods, { from: '01-01', to: '01-01' }],
    })
  }

  function removeClosedPeriod(index: number) {
    onChange({
      ...seasonality,
      closed_periods: seasonality.closed_periods.filter((_, i) => i !== index),
    })
  }

  return (
    <section
      className="space-y-3 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] p-3"
      data-testid="route-seasonality-editor"
    >
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--xr-text-muted)]">
          适合季节
        </h3>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SEASONS.map((season) => {
            const active = seasonality.best_seasons.includes(season)
            return (
              <button
                key={season}
                type="button"
                onClick={() => setBestSeason(season, !active)}
                className={
                  active
                    ? 'inline-flex h-[26px] items-center rounded-full bg-[var(--xr-brand-primary-soft)] px-2.5 text-[11px] font-medium text-[var(--xr-brand-primary)]'
                    : 'inline-flex h-[26px] items-center rounded-full border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2.5 text-[11px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)]'
                }
                aria-pressed={active}
              >
                {seasonLabel(season)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--xr-text-muted)]">
            限期关闭
          </h3>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addClosedPeriod}
            disabled={seasonality.closed_periods.length >= 20}
          >
            添加
          </Button>
        </div>
        {seasonality.closed_periods.length === 0 ? (
          <p className="text-xs text-[var(--xr-text-muted)]">暂无关闭期。</p>
        ) : (
          <div className="space-y-2">
            {seasonality.closed_periods.map((period, index) => (
              <div
                key={index}
                className="grid grid-cols-[minmax(0,4.5rem)_minmax(0,4.5rem)_minmax(0,1fr)_auto] gap-2"
              >
                <Input
                  value={period.from}
                  onChange={(event) => setClosedPeriod(index, { from: event.target.value })}
                  placeholder="MM-DD"
                  pattern="[0-9]{2}-[0-9]{2}"
                  aria-label={t('aria.closedPeriodStartDate')}
                />
                <Input
                  value={period.to}
                  onChange={(event) => setClosedPeriod(index, { to: event.target.value })}
                  placeholder="MM-DD"
                  pattern="[0-9]{2}-[0-9]{2}"
                  aria-label={t('aria.closedPeriodEndDate')}
                />
                <Input
                  value={period.reason ?? ''}
                  onChange={(event) =>
                    setClosedPeriod(index, { reason: event.target.value || undefined })
                  }
                  maxLength={200}
                  placeholder="原因"
                  aria-label={t('aria.closedPeriodReason')}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => removeClosedPeriod(index)}
                >
                  移除
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function isDateInClosedPeriod(period: ClosedPeriod, date: Date): boolean {
  const from = mmddToOrdinal(period.from)
  const to = mmddToOrdinal(period.to)
  if (from == null || to == null) return false
  const current = (date.getMonth() + 1) * 100 + date.getDate()
  if (from <= to) return current >= from && current <= to
  return current >= from || current <= to
}

function mmddToOrdinal(value: string): number | null {
  const match = /^(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const month = Number(match[1])
  const day = Number(match[2])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return month * 100 + day
}
